import type { DetailsHTMLAttributes, ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { CaretRightIcon } from "./icons";

export interface DisclosureProps extends Omit<DetailsHTMLAttributes<HTMLDetailsElement>, "title"> {
  title: ReactNode;
  summary?: ReactNode;
}

/** Native disclosure: the whole 40px header is the target and state persists natively. */
export function Disclosure({ title, summary, children, className, ...props }: DisclosureProps) {
  return (
    <details className={cn("studio-disclosure group rounded-xl border border-line bg-paper", className)} {...props}>
      <summary className="flex min-h-11 list-none items-center gap-2.5 px-3 text-[13px] font-medium text-ink outline-none marker:content-none hover:text-accent-text focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)] sm:min-h-10 [&::-webkit-details-marker]:hidden">
        <CaretRightIcon className="size-4 shrink-0 text-ink-muted motion-safe:transition-transform motion-safe:duration-150 group-open:rotate-90" />
        <span className="min-w-0 flex-1 font-medium group-open:font-semibold">{title}</span>
        {summary ? <span className="text-xs font-normal text-ink-muted">{summary}</span> : null}
      </summary>
      <div className="border-t border-line-soft px-3 py-3 ps-9 text-[13px] text-ink-soft">{children}</div>
    </details>
  );
}

export function ScrollContainer({ children, className, label }: { children: ReactNode; className?: string; label: string }) {
  return (
    <div tabIndex={0} role="region" aria-label={label} className={cn("max-w-full overflow-auto overscroll-contain rounded-sm border border-line-soft focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]", className)}>
      {children}
    </div>
  );
}
